import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'

import { formatDate } from 'helpers/helpers'
import { httpOk, GET, POST, DELETE } from 'helpers/api'

function ModalTaskDetail({
	apiPrefix,
	detailItem,
	statuses = [],
	canManage = false,
	// Править лид может и соисполнитель, удалять — только автор
	canEdit = false,
	currentUserLogin = null,
	onEdit = null,
	onDelete = null,
	onNotesChanged = null,
	modalDetail,
	toggleModalDetail,
}) {
	const { t } = useTranslation()

	const [task, setTask] = useState(detailItem)
	const [notes, setNotes] = useState(detailItem.notes || [])
	const [note, setNote] = useState('')

	async function getTask() {
		const response = await GET(`${apiPrefix}/tasks/${detailItem.id}`, {}, { loader: true })
		if (httpOk(response)) {
			setTask({ ...detailItem, ...response.data })
			setNotes(response.data?.notes || [])
		}
	}

	// Примечания только копятся: новое ложится сверху, старые остаются на месте.
	// Доску не перезагружаем — счётчик на карточке правим точечно
	function applyNotes(nextNotes) {
		setNotes(nextNotes)
		if (typeof onNotesChanged === 'function') onNotesChanged(task.id, nextNotes)
	}

	async function addNote(e) {
		e.preventDefault()
		if (!note.trim()) return

		const response = await POST(`${apiPrefix}/tasks-note`, {
			'task_id': task.id,
			'note': note.trim(),
		}, { loader: true, notification: false })

		if (httpOk(response)) {
			applyNotes(response.data?.notes || [])
			setNote('')
		}
	}

	async function deleteNote(item) {
		const response = await DELETE(`${apiPrefix}/tasks-note/${item.id}`, {}, { loader: true })
		if (httpOk(response)) applyNotes(response.data?.notes || [])
	}

	// Своё примечание можно убрать, чужое и системное — нет. Бэк проверяет то же самое
	function canDeleteNote(item) {
		if (item.source !== 'manual') return false
		if (canManage) return true
		return !!currentUserLogin && String(item.created_by) === String(currentUserLogin)
	}

	function returnStatusColor(statusId) {
		const found = statuses.find(item => item.id === statusId)
		return found?.color || '#5b73e8'
	}

	function returnPos() {
		if (!task.pos_id) return '-'

		const title = task.pos_name || task.pos_short_name
		return title ? title + ' · ID ' + task.pos_id : 'ID ' + task.pos_id
	}

	function returnInitial(item) {
		const source = item.name || item.login || ''
		return source.trim().charAt(0).toUpperCase()
	}

	// Что именно произошло в этой строке истории — собираем из полей, которые есть в ответе
	function returnHistoryTitle(item) {
		if (item.status_name_to) {
			return item.status_name_from
				? item.status_name_from + ' → ' + item.status_name_to
				: item.status_name_to
		}
		if (item.deadline_to) {
			return t('task_new_deadline') + ': ' + formatDate(item.deadline_to, 'dd.MM.yyyy')
		}
		return t('task_history')
	}

	function returnHistoryExtra(item) {
		// Перенос срока показываем отдельной строкой, если статус тоже менялся
		if (item.status_name_to && item.deadline_to) {
			return t('task_new_deadline') + ': ' + formatDate(item.deadline_to, 'dd.MM.yyyy')
		}
		return null
	}

	const rows = [
		{ 'label': t('organization'), 'value': task.client_name, 'extra': task.client_login },
		{ 'label': t('pos'), 'value': returnPos() },
		{ 'label': t('task_type'), 'value': task.category_name },
		{ 'label': t('task_service'), 'value': task.service_name },
		{ 'label': t('created_at'), 'value': task.created_at ? formatDate(task.created_at, 'dd.MM.yyyy HH:mm') : null, 'extra': task.created_by },
	]

	// Данные клиента показываем только если их заполнили: у задач с точкой они пустые
	const clientRows = [
		{ 'label': t('task_client_phone'), 'value': task.client_phone },
		{ 'label': t('task_director_phone'), 'value': task.director_phone },
		{ 'label': t('address'), 'value': task.address },
		{ 'label': t('task_direction'), 'value': task.direction },
		{ 'label': t('task_lead_source'), 'value': task.lead_source },
	].filter(row => row.value)

	useEffect(() => {
		if (detailItem.id) getTask()
	}, [modalDetail]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Modal show={modalDetail} animation={false} centered
			dialogClassName="update-modal-width tasks-modal tasks-detail-modal"
			onHide={() => toggleModalDetail()}>
			<Modal.Header closeButton>
				<div>
					<div className="tasks-detail-chips">
						<span className="tasks-detail-chip">ID {task.id}</span>
						{task.status_name &&
							<span className="tasks-detail-chip"
								style={{
									background: returnStatusColor(task.task_status_id) + '1f',
									color: returnStatusColor(task.task_status_id),
								}}>
								<span className="tasks-column-dot"
									style={{ background: returnStatusColor(task.task_status_id) }}></span>
								{task.status_name}
							</span>
						}
						{task.is_overdue &&
							<span className="tasks-detail-chip is-danger">{t('overdue')}</span>
						}
						{task.source === 'system' &&
							<span className="tasks-detail-chip is-auto" title={t('task_source_system_hint')}>
								<i className="uil uil-bolt-alt"></i>
								{t('task_source_system')}
							</span>
						}
					</div>
					<div className="tasks-detail-name">{task.name}</div>
				</div>
			</Modal.Header>
			<Modal.Body>
				<div className="row">

					<div className="col-md-7">
						{task.description &&
							<div className="mb-3">
								<label className="tasks-modal-label">{t('description')}</label>
								<div className="tasks-detail-text">{task.description}</div>
							</div>
						}

						<div className="mb-3">
							<label className="tasks-modal-label">{t('information')}</label>
							<div className="tasks-detail-table">
								{rows.map((row, index) => (
									<div className="tasks-detail-row" key={index}>
										<div className="tasks-detail-label">{row.label}</div>
										<div className="tasks-detail-value">
											{row.value || '-'}
											{row.value && row.extra &&
												<span className="tasks-detail-muted"> · {row.extra}</span>
											}
										</div>
									</div>
								))}
							</div>
						</div>

						{clientRows.length > 0 &&
							<div className="mb-3">
								<label className="tasks-modal-label">{t('task_client_data')}</label>
								<div className="tasks-detail-table">
									{clientRows.map((row, index) => (
										<div className="tasks-detail-row" key={index}>
											<div className="tasks-detail-label">{row.label}</div>
											<div className="tasks-detail-value">{row.value}</div>
										</div>
									))}
								</div>
							</div>
						}

						{/* Примечания — лента звонков и системных событий: пишем сюда, читаем всю переписку */}
						<div className="mb-3">
							<label className="tasks-modal-label">
								{t('task_notes')}
								{notes.length > 0 &&
									<span className="tasks-detail-count">{notes.length}</span>
								}
							</label>

							<form onSubmit={addNote} className="tasks-note-form">
								<textarea className="form-control" rows={2}
									placeholder={t('task_note_placeholder')}
									value={note}
									onChange={(e) => setNote(e.target.value)}>
								</textarea>
								<button type="submit" className="btn btn-primary" disabled={!note.trim()}>
									{t('task_note_add')}
								</button>
							</form>

							{notes.length === 0 &&
								<div className="tasks-detail-empty">{t('task_note_empty')}</div>
							}

							{notes.length > 0 &&
								<div className="tasks-note-list">
									{notes.map((item, index) => (
										<div className={'tasks-note-item' + (item.source !== 'manual' ? ' is-system' : '')}
											key={index}>
											<div className="tasks-note-head">
												<span className="tasks-note-author">
													{item.source !== 'manual'
														? t('task_note_system')
														: item.created_by
													}
												</span>
												<span className="tasks-note-date">
													{item.created_at ? formatDate(item.created_at, 'dd.MM.yyyy HH:mm') : ''}
												</span>
												{canDeleteNote(item) &&
													<button type="button" className="tasks-note-delete"
														title={t('delete')}
														onClick={() => deleteNote(item)}>
														<i className="uil uil-trash-alt"></i>
													</button>
												}
											</div>
											<div className="tasks-note-text">{item.note}</div>
										</div>
									))}
								</div>
							}
						</div>

						<div>
							<label className="tasks-modal-label">
								{t('task_history')}
								{task.histories?.length > 0 &&
									<span className="tasks-detail-count">{task.histories.length}</span>
								}
							</label>
							{(!task.histories || task.histories.length === 0) &&
								<div className="tasks-detail-empty">{t('no_data')}</div>
							}
							{/* История растёт бесконечно, поэтому скроллим её внутри, а не растягиваем модалку */}
							{task.histories?.length > 0 &&
								<div className="tasks-history-list">
									{task.histories.map((item, index) => (
										<div className="tasks-history-item" key={index}>
											<span className="tasks-history-dot"
												style={{ background: returnStatusColor(item.task_status_id_to) }}></span>
											<div className="tasks-history-text">{returnHistoryTitle(item)}</div>
											<div className="tasks-history-date">
												{item.created_at ? formatDate(item.created_at, 'dd.MM.yyyy HH:mm') : ''}
												{item.created_by ? ' · ' + item.created_by : ''}
											</div>
											{returnHistoryExtra(item) &&
												<div className="tasks-history-date">{returnHistoryExtra(item)}</div>
											}
											{item.comment &&
												<div className="tasks-history-comment">«{item.comment}»</div>
											}
										</div>
									))}
								</div>
							}
						</div>
					</div>

					<div className="col-md-5">
						<div className="tasks-detail-side">
							<div className="mb-3">
								<label className="tasks-modal-label">
									{t('task_executors')}
									{task.users?.length > 1 &&
										<span className="tasks-detail-count">{task.users.length}</span>
									}
								</label>
								{(!task.users || task.users.length === 0) &&
									<div className="tasks-detail-empty">{t('no_data')}</div>
								}
								{task.users?.map((item, index) => (
									<div className="tasks-detail-user" key={index}>
										<span className="tasks-detail-avatar">{returnInitial(item)}</span>
										<div className="tasks-detail-user-info">
											<div className="tasks-detail-user-name">
												{item.name || item.login}
												{/* Автор ведёт лид, остальные подключены к продаже */}
												{String(item.login) === String(task.created_by) &&
													<span className="tasks-detail-muted"> · {t('task_author')}</span>
												}
											</div>
											<div className="tasks-detail-muted">
												{item.login}
												{item.phone ? ' · ' + item.phone : ''}
											</div>
										</div>
									</div>
								))}
							</div>

							<div>
								<label className="tasks-modal-label">{t('deadline')}</label>
								<div className="tasks-detail-box">
									<div className="tasks-detail-deadline">
										{task.deadline ? formatDate(task.deadline, 'dd.MM.yyyy') : '-'}
									</div>
									{task.is_overdue &&
										<span className="tasks-detail-chip is-danger mt-2">{t('overdue')}</span>
									}
									{task.previous_deadline &&
										<div className="tasks-detail-muted mt-2">
											{t('task_previous_deadline')}: {formatDate(task.previous_deadline, 'dd.MM.yyyy')}
										</div>
									}
									{task.completed_at &&
										<div className="tasks-detail-muted mt-2">
											{t('task_completed')}: {formatDate(task.completed_at, 'dd.MM.yyyy HH:mm')}
										</div>
									}
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="tasks-modal-footer">
					{!canEdit &&
						<div className="tasks-detail-muted me-auto">{t('task_only_author_can_edit')}</div>
					}
					{canEdit && !canManage &&
						<div className="tasks-detail-muted me-auto">{t('task_only_author_can_delete')}</div>
					}
					<button type="button" className="btn btn-outline-secondary btn-wide"
						onClick={() => toggleModalDetail()}>
						{t('cancel')}
					</button>
					{canManage &&
						<button type="button" className="btn btn-outline-danger btn-wide"
							onClick={() => onDelete(task)}>
							{t('delete')}
						</button>
					}
					{canEdit &&
						<button type="button" className="btn btn-primary btn-wide"
							onClick={() => onEdit(task)}>
							{t('edit')}
						</button>
					}
				</div>
			</Modal.Body>
		</Modal>
	)
}

export default ModalTaskDetail
